import { useEffect, useState } from 'react';
import { useOrgSettings } from '@/hooks/use-settings';

/**
 * THE NEW REPORTS, behind a switch (reports plan, 13 Oct 2026).
 *
 * The owner could not tell which number lived where: 45 report pages grew one
 * service at a time, so the same question had several homes. The new shape
 * groups them by question (Home, Performance, Store, Tracking) with one
 * explorer per thing you slice by. It ships behind a per-workspace switch so
 * nothing moves for a client until the owner has looked at it:
 *
 *   org_settings 'reports.v2' = true   the workspace can see the new reports
 *   REACT_APP_REPORTS_V2=1             every workspace can (a local review build)
 *   ?reports_preview=1 on any page     this browser can, live, in any workspace
 *                                      (the owner's review link, 16 Oct 2026; a
 *                                      client never sees it without the link;
 *                                      ?reports_preview=0 turns it off)
 *
 * Inside a workspace that has them, each person can still flip back to the
 * current reports from the foot of the sidebar; the choice is per browser.
 * Every current page keeps its address and keeps working either way.
 */

const PREF = 'mm.reports-view';
const PREVIEW = 'mm.reports-preview';

// The review link: ?reports_preview=1 remembers itself in this browser.
const readPreview = () => {
  try {
    const q = new URLSearchParams(window.location.search).get('reports_preview');
    if (q === '1') localStorage.setItem(PREVIEW, '1');
    if (q === '0') localStorage.removeItem(PREVIEW);
    return localStorage.getItem(PREVIEW) === '1';
  } catch { return false; }
};
const EVT = 'mm:reports-view';

const readPref = () => {
  try { return localStorage.getItem(PREF) || 'new'; } catch { return 'new'; }
};

// Read outside React (the page header, GTable) without a hook of its own.
// Written by useReportsV2 on every render that knows the answer.
let live = false;
export const reportsV2Live = () => live;

export function setReportsView(view) {
  try { localStorage.setItem(PREF, view); } catch { /* private window */ }
  window.dispatchEvent(new Event(EVT));
}

export function useReportsV2() {
  const { data } = useOrgSettings();
  const [preview] = useState(readPreview);
  const available = data?.values?.['reports.v2'] === true || process.env.REACT_APP_REPORTS_V2 === '1' || preview;
  const [pref, setPref] = useState(readPref);
  useEffect(() => {
    const h = () => setPref(readPref());
    window.addEventListener(EVT, h);
    window.addEventListener('storage', h);
    return () => { window.removeEventListener(EVT, h); window.removeEventListener('storage', h); };
  }, []);
  const on = available && pref !== 'current';
  live = on;
  return { available, on, setView: setReportsView };
}
